import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	AlertProps,
	Box,
	Button,
	Flashbar,
	Icon,
	LiveRegion,
	Modal,
	Popover,
	SpaceBetween,
	Toggle,
	Wizard,
	WizardProps,
	Container,
	Header,
} from '@amzn/awsui-components-react-v3';
import { useHistory, useParams } from 'react-router';
import { SFNClient, StartSyncExecutionCommand } from '@aws-sdk/client-sfn';
import { v4 as uuidv4 } from 'uuid';

import ErrorLoader from 'src/components/commonComponents/errorLoader/errorLoader';
import WatcherInput from 'src/components/commonComponents/watcherInput/watcherInput';

import {
	GetWorkflowDefinitionResponse,
	StartWorkflowExecutionResponse,
	TaskDefinition,
	WorkflowDefinition,
	WorkflowIntention,
} from '@amzn/snow-nxt-workflow-service-typescript-client';

import { ApprovalType, CreateApprovalResponse } from '@amzn/snow-data-service-typescript-client';

import {
	CreateEntityResponse,
	SNOWEntity,
} from 'src/@types/snowDataServiceTypes/createEntity.interface';

import {AuthorizeAPIResponse, EntityAPIResponse} from 'src/@types/SNOWDataServiceTypes';
import {
	AMAZON_APPROVAL_ENDPOINT,
	IBFR_STATE_MACHINE_ARN,
	IBFR_UPDATE_TASK_ID,
	IBFR_WORKFLOW_NAME,
	SDS_APIGATEWAY_ENDPOINT,
	SNOW_NXT_WORKFLOW_ENDPOINT,
	SNOW_VALIDATION_SERVICE_ENDPOINT,
} from 'src/config';
import {
	generateFormSteps,
	generateRequirements,
	getWorkflowStartRedirectionLink,
	IFBRValidationStatus,
} from './requirementsFormGeneratorUtils';
import {
	buildSubWorkflowEntities,
	retryOnError,
	SUB_WORKFLOW_ENTITY_RETRY_DELAYS_MS,
	withSubWorkflowInputs,
} from './subWorkflowRequirementUtils';
import { loadLayoutFromDefinition } from 'src/components/LayoutEditor/layoutStorageService';
import { generateStepsFromLayout } from 'src/components/LayoutEditor/LayoutRenderer';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { useAuthContext } from 'src/providers/AuthContext';
import { OLAF_WORKFLOW_BINDLE_DATA } from 'src/authentication/bindle-utils';
import { LoadingOverlay } from 'src/components/commonComponents/loadingOverlay/loadingOverlay';
import { KeyStringValuePairsObject } from '../../../@types/common';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import AWS from 'aws-sdk';
import { getGroupIdByWorkflow } from 'src/constants/WorkflowDetails';
import { WorkflowStatus } from 'src/constants/commonEnums';
import {LoadingBar} from '@amzn/awsui-chat-components-console';
import {
	AutoPopulationProvider,
	useAutoPopulation,
} from 'src/components/selfServeWorkflows/requirementsPortal/autoPopulation/AutoPopulationProvider';
import {
	extractEntityValuesForAutopopulation,
	mergeRequirementsWithEntityValues,
} from 'src/components/selfServeWorkflows/requirementsPortal/autoPopulation/autoPopulationUtils';

import { useGlobalExecutionGate } from 'src/hooks/useGlobalExecutionGate';
import './RequirementsPortal.scss';

enum ActionType {
	VALIDATION = 'validation',
	APPROVAL = 'approval',
	WORKFLOW = 'workflow',
}

interface IbfrApiResponse {
	EntityName: string;
	oldConfig?: string,
	newConfig?: string
}

const RequirementsPageContent = ({ setTaskDefinitions }: { setTaskDefinitions: (taskDefs: any[]) => void }) => {
	const { isEffectivelyBlocked } = useGlobalExecutionGate();
	const { handleAutoPopulation, enrichRequirements, isAutoPopulating, autoPopulationError, setAutoPopulationError, baselineValues, setWorkflowIntention } = useAutoPopulation();
	const methods = useFormContext();
	const [activeStepIndex, setActiveStepIndex] = useState(0);
	const [isCustomLayoutViewEnabled, setIsCustomLayoutViewEnabled] = useState(true);
	const [ifbrLoader, setIfbrLoader] = useState(false);
	const [ifbrReqeustValid, setIfbrReqeustValid] = useState(false);
	const [approvalsRequest, setApprovalsRequest] = useState({
		newConfig: '',
		oldConfig: '',
	});
	const [apiResponse, setApiResponse] = useState<IbfrApiResponse>({
		EntityName: '',
	});
	const [ ifbrAlert, setIFBRAlert ] = useState({
		message: '',
		type: '',
		enabled: true,
		isLoading: false,
	});
	const [ validateIFBRInputLoader, setValidateIFBRInputLoader ] = useState<IFBRValidationStatus>(IFBRValidationStatus.NotStarted);
	const [showCancelModal, setShowCancelModel] = useState(false);
	const [showLaunchModal, setShowLaunchModel] = useState(false);
	const [watchers, setWatchers] = useState<string[]>([]);
	const [isLoadingNextStep, setIsLoadingNextStep] = useState(false);
	const [redirectUrl, setRedirectUrl] = useState('');
	const timeOutRef = useRef<ReturnType<typeof setTimeout>>();

	// Stable request IDs pinned to component lifetime so identity-equal
	// fetch arguments don't accidentally cause re-fetches if the hook ever
	// starts depending on body/queryParams.
	const authorizeRequestId = useRef(uuidv4()).current;
	const entityFetchRequestId = useRef(uuidv4()).current;

	const history = useHistory();
	const { workflowType } = useParams<KeyStringValuePairsObject>();

	const queryParams = new URLSearchParams(history.location.search);
	const entityName = queryParams.get('entityName') || undefined;

	const { userName } = useAuthContext();
	const mainFormMethods = useFormContext();

	const {
		isLoading: isAuthLoading,
		error: authError,
		data: authResponse,
	} = useFetchForSDS<AuthorizeAPIResponse>({
		path: '/authorize',
		method: 'POST',
		body: {
			user: userName,
			resource: {
				...OLAF_WORKFLOW_BINDLE_DATA,
				ids: [workflowType],
			},
			requestId: authorizeRequestId,
		},
	});

	const {
		isLoading: isEntityDataLoading,
		error: entityDataError,
		data: entityDataResponse,
	} = useFetchForSDS<EntityAPIResponse<Record<string, unknown>>>({
		path: entityName ? '/entity' : '',
		method: 'GET',
		queryParams: entityName ? {
			entityName: entityName,
			workflowType: workflowType,
			fetchAllAttributes: true,
			requestId: entityFetchRequestId,
		} : {},
	});

	const {
		result: {
			isLoading: isWorkflowDataLoading,
			error: workflowDataError,
			data: getWorkflowDefinitionData,
		},
		onClickCB: onAuthCompletionCB,
	} = useFetchOnUserActionUsingSDS<GetWorkflowDefinitionResponse>();

	useEffect(() => {
		if (authResponse)
			onAuthCompletionCB({
				path: `/workflows/${workflowType}`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
			});
	}, [authResponse]);

	const handleIndexChange = async (requestedIndex: number) => {

		if (!AWS.config.credentials) {
			setValidateIFBRInputLoader(IFBRValidationStatus.Error);

			return;
		}

		const sfnClient = new SFNClient({
			region: 'us-east-1',
			credentials: {
				accessKeyId: AWS.config.credentials.accessKeyId,
				secretAccessKey: AWS.config.credentials.secretAccessKey,
				sessionToken: AWS.config.credentials.sessionToken,
			  },
		});

		if (workflowName === IBFR_WORKFLOW_NAME) {
			let configKey;
			let configKeyArray: string[];

			switch (requestedIndex) {
			case 1:
				setIfbrLoader(true);
				configKey = mainFormMethods.getValues().EntityData.keys;

				if (typeof configKey !== 'string' || configKey === '') {
					    configKeyArray = [];
				} else {
					    configKeyArray = configKey.split(',').map(item => item.trim());
				}

				const queryPayload = {
					operation: 'query',
					requestBody: {
						  configType: mainFormMethods.getValues().EntityData.configType,
						  metadataStore: {},
						  keys: configKeyArray,
						  version: 1,
					},
				};

				const startQueryCallCommand = new StartSyncExecutionCommand({
					stateMachineArn: IBFR_STATE_MACHINE_ARN,
					input: JSON.stringify(queryPayload),
				});

				const response = await sfnClient.send(startQueryCallCommand)
					.catch((ex) => {
						setIfbrLoader(false);
						setValidateIFBRInputLoader(IFBRValidationStatus.Error);
						throw new Error(ex.message);
					});


				if (response.status !== 'SUCCEEDED' || !response.output) {
					setValidateIFBRInputLoader(IFBRValidationStatus.QueryFailed);
					setIfbrLoader(false);
					throw new Error('failed to call /query to fetch config records');
				}

				try {
					const newConfig = JSON.parse(response.output).output.ResponseBody.config;

					setApprovalsRequest({
						newConfig: JSON.stringify(newConfig),
						oldConfig: JSON.stringify(newConfig),
					});
					setApiResponse((prevState) => ({
						...prevState,
						['newConfig']: JSON.stringify(newConfig).slice(1, -1),
						['oldConfig']: JSON.stringify(newConfig).slice(1, -1),
					}));
					setIfbrLoader(false);
				} catch (error) {
					setValidateIFBRInputLoader(IFBRValidationStatus.NoConfigFound);
					setIfbrLoader(false);
					throw new Error('config not found for given request');
				}

				break;
			case 2:
				setIfbrLoader(true);
				setValidateIFBRInputLoader(IFBRValidationStatus.Loading);

				configKey = mainFormMethods.getValues().EntityData.keys;
				if (typeof configKey !== 'string' || configKey === '') {
					    configKeyArray = [];
				} else {
					    configKeyArray = configKey.split(',').map(item => item.trim());
				}

				setIfbrReqeustValid(false);
				const validatePayload = {
					operation: 'validate',
  						requestBody: {
  						  configType: mainFormMethods.getValues().EntityData.configType,
  						  metadataStore: {},
  						  keys: configKeyArray,
  						  version: 1,
						  newConfig: JSON.parse(`"${mainFormMethods.getValues()[IBFR_UPDATE_TASK_ID]?.newConfig}"`),
					},
				};

				const startValidateCallCommand = new StartSyncExecutionCommand({
					stateMachineArn: IBFR_STATE_MACHINE_ARN,
					input: JSON.stringify(validatePayload),
				});

				const startValidateCallResponse = await sfnClient.send(startValidateCallCommand)
					.catch((ex) => {
						setValidateIFBRInputLoader(IFBRValidationStatus.Error);
						setIfbrLoader(false);
						throw new Error(ex.message);
					});

				if (startValidateCallResponse.status !== 'SUCCEEDED' || !startValidateCallResponse.output) {
					setValidateIFBRInputLoader(IFBRValidationStatus.ValidationFailed);
					setIfbrLoader(false);
					throw new Error('failed to call /validate to validate new config');
				}

				try {
					const validateResponse = JSON.parse(startValidateCallResponse.output).output.ResponseBody;

					if (validateResponse.configValid) {
						  setValidateIFBRInputLoader(IFBRValidationStatus.Success);
					} else {
						  setValidateIFBRInputLoader(IFBRValidationStatus.ValidationFailed);
						  setIfbrLoader(false);
						  throw new Error('failed to validate new config');
					}
					  } catch (error) {
					setIfbrLoader(false);
					setValidateIFBRInputLoader(IFBRValidationStatus.ValidationFailed);
					throw new Error('Failed to parse the validation response.');
					  }

					  setIfbrReqeustValid(true);
					  setIfbrLoader(false);
				break;
			default:
				break;
			}
		}
	};

	const {
		result: {
			isLoading: isEntityCreating,
			flashBarItems: createEntityFlashBarItems,
		},
		onClickCB: onCreateEntityCB,
	} = useFetchOnUserActionUsingSDS<CreateEntityResponse>();

	const {
		result: {
			isLoading: isWorkflowStarting,
			flashBarItems: workflowStartFlashbarItems,
		},
		onClickCB: onWorkflowStartCB,
	} = useFetchOnUserActionUsingSDS<StartWorkflowExecutionResponse>();

	const {
		result: {
			isLoading: isValidationInitiating,
			flashBarItems: validationInitiateFlashbarItems,
		},
		onClickCB: onValidationInitiateCB,
	} = useFetchOnUserActionUsingSDS<any>();

	const {
		result: {
			isLoading: isSendForApprovalLoading,
			flashBarItems: sendForApprovalFlashBarItems,
			data: approvalResponse,
		},
		onClickCB: onSendForApprovalCB,
	} = useFetchOnUserActionUsingSDS<CreateApprovalResponse>();

	const taskDefinitions: TaskDefinition[] = useMemo(
		() => getWorkflowDefinitionData?.taskDefinitions || [],
		[getWorkflowDefinitionData]
	);

	useEffect(() => {
		setTaskDefinitions(taskDefinitions);
	}, [taskDefinitions, setTaskDefinitions]);

	// The form also collects the inputs of every embedded sub-workflow: the first step merges
	// their workflow-level inputs with the parent's, and each sub-workflow gets a task-specific
	// step of its own. Without sub-workflows this is just taskDefinitions.
	const workflowTreeTasks = useMemo(
		() =>
			withSubWorkflowInputs(
				taskDefinitions,
				getWorkflowDefinitionData?.subWorkflows
			),
		[taskDefinitions, getWorkflowDefinitionData]
	);

	const workflowDefinition: WorkflowDefinition | undefined =
		getWorkflowDefinitionData?.workflowDefinition;

	const workflowName = workflowDefinition?.workflowDefinitionName;
	const entityType = workflowDefinition?.entityType;

	// Custom portal layout is delivered on the workflow definition API response as
	// a stringified JSON blob under `portalLayout`. loadLayoutFromDefinition parses
	// it and returns null when the workflow has no custom layout configured.
	const customLayout = loadLayoutFromDefinition(getWorkflowDefinitionData);

	useEffect(() => {
		if (workflowName === IBFR_WORKFLOW_NAME) {
			setApiResponse({
				EntityName: new Date().getTime().toString(),
			});
		}
	}, [workflowName]);

	useEffect(() => {
		setWorkflowIntention(workflowDefinition?.workflowIntention as WorkflowIntention | undefined);
	}, [workflowDefinition?.workflowIntention]);

	// TODO: Need to change this to fetch requirements from requirements API.

	const entityValuesForAutopopulation = useMemo(
		() =>
			extractEntityValuesForAutopopulation(
				entityDataResponse,
				taskDefinitions,
				workflowTreeTasks.subWorkflowGroups
			),
		[entityDataResponse, taskDefinitions, workflowTreeTasks.subWorkflowGroups]
	);

	useEffect(() => {
		if (!taskDefinitions.length) return;

		const baseRequirements = generateRequirements(workflowTreeTasks.allTaskDefinitions, workflowType, entityType!);
		const hasEntityValues =
			entityName && Object.keys(entityValuesForAutopopulation).length > 0;

		if (hasEntityValues) {
			const mergedValues = mergeRequirementsWithEntityValues(
				baseRequirements,
				entityValuesForAutopopulation
			);
			methods.reset(mergedValues, { keepDefaultValues: false });

			const entityDataValues =
				(entityValuesForAutopopulation as { EntityData?: Record<string, unknown> })
					.EntityData ?? {};
			Object.keys(entityDataValues).forEach(key => {
				methods.setValue(`EntityData.${key}` as any, entityDataValues[key]);
			});
		} else {
			methods.reset(baseRequirements, { keepDefaultValues: false });
		}
	}, [
		entityValuesForAutopopulation,
		entityName,
		taskDefinitions,
		workflowTreeTasks,
		workflowType,
		entityType,
		methods.reset,
		methods.setValue,
	]);

	const hasValidationDefinitions = workflowTreeTasks.allTaskDefinitions.some(
		task => task.validationDefinitions && task.validationDefinitions.length > 0
	);

	type WorkflowMode = 'approvalAndValidation' | 'approvalOnly' | 'validationOnly' | 'directStart';

	const getWorkflowMode = (): WorkflowMode => {
		const hasApproval = workflowDefinition?.approvalRequiredToStartExecution;
		const hasValidation = hasValidationDefinitions;

		if (hasApproval && hasValidation) return 'approvalAndValidation';
		if (hasApproval) return 'approvalOnly';
		if (hasValidation) return 'validationOnly';
		return 'directStart';
	};

	const WORKFLOW_LABELS: Record<WorkflowMode, { submitButton: string; modalButton: string; modalMessage: string }> = {
		approvalAndValidation: {
			submitButton: 'Submit for Validation',
			modalButton: 'Submit for Validation',
			modalMessage: 'By submitting for validation, you hereby acknowledge and affirm that you have checked all the required inputs and to the fullest extent of your knowledge, meet the necessary standards. After successful validation, the workflow will be sent for approval.',
		},
		approvalOnly: {
			submitButton: 'Send for Approval',
			modalButton: 'Send For Approval',
			modalMessage: 'By sending for approval, you hereby acknowledge and affirm that you have checked all the required inputs and to the fullest extent of your knowledge, meet the necessary standards for approval.',
		},
		validationOnly: {
			submitButton: 'Submit for Validation',
			modalButton: 'Submit for Validation',
			modalMessage: 'By submitting for validation, you hereby acknowledge and affirm that you have checked all the required inputs and to the fullest extent of your knowledge, meet the necessary standards. After successful validation, the workflow execution will start automatically.',
		},
		directStart: {
			submitButton: 'Start Workflow',
			modalButton: 'Launch Workflow',
			modalMessage: 'By starting the workflow, you hereby acknowledge and affirm that you have checked all the required inputs and to the fullest extent of your knowledge, meet the necessary standards.',
		},
	};

	const workflowMode = getWorkflowMode();
	const labels = WORKFLOW_LABELS[workflowMode];

	const i18nStrings = {
		stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
		collapsedStepsLabel: (stepNumber, stepsCount) =>
			`Step ${stepNumber} of ${stepsCount}`,
		skipToButtonLabel: (step) => `Skip to ${step.title}`,
		navigationAriaLabel: 'Steps',
		cancelButton: 'Cancel',
		previousButton: 'Previous',
		nextButton: 'Next',
		submitButton: labels.submitButton,
		optional: 'optional',
	};

	useEffect(() => {
		if (validateIFBRInputLoader === IFBRValidationStatus.Error) {
			setIFBRAlert({
				message: 'Caught error while trying to call IBFR',
				type: 'error',
				enabled: true,
				isLoading: false,
			});
		} else if (validateIFBRInputLoader === IFBRValidationStatus.NoConfigFound) {
			setIFBRAlert({
				message: 'No config found for the provided keys',
				type: 'error',
				enabled: true,
				isLoading: false,
			});
		} else if (validateIFBRInputLoader === IFBRValidationStatus.Loading) {
			setIFBRAlert({
				message: 'Validting IFBR input config',
				type: 'info',
				enabled: true,
				isLoading: true,
			});
		} else if (validateIFBRInputLoader === IFBRValidationStatus.QueryFailed) {
			setIFBRAlert({
				message: 'Failed to to query configurations from IBFR',
				type: 'error',
				enabled: true,
				isLoading: true,
			});
		} else if (validateIFBRInputLoader === IFBRValidationStatus.Success) {
			setIFBRAlert({
				message: 'Successfully validated input to update IBFR configurations',
				type: 'success',
				enabled: true,
				isLoading: false,
			});
		} else if (validateIFBRInputLoader === IFBRValidationStatus.ValidationFailed) {
			setIFBRAlert({
				message: 'Validation failed for provided configurations',
				type: 'error',
				enabled: true,
				isLoading: true,
			});
		}
	}, [validateIFBRInputLoader]);

	let entityValue:SNOWEntity;
	const createEntityAndStartWorkflow = async (entity: SNOWEntity) => {
		const creationTime = new Date(Date.now()).toISOString();
		const groupIdAttribute = getGroupIdByWorkflow(workflowDefinition?.workflowId);

		let entityStatus = WorkflowStatus.READY;
		if (workflowDefinition?.approvalRequiredToStartExecution) {
			entityStatus = WorkflowStatus.DRAFT;
		} else if (hasValidationDefinitions) {
			entityStatus = WorkflowStatus.DRAFT;
		}

		const { parentEntity, subWorkflowEntities } = buildSubWorkflowEntities(workflowTreeTasks, entity);

		const isApprovalOnly = workflowDefinition?.approvalRequiredToStartExecution && !hasValidationDefinitions;

		const buildEntityValue = (
			entityToBuild: SNOWEntity,
			definition: WorkflowDefinition | undefined,
			groupId: string,
			status = entityStatus
		): SNOWEntity => ({
			...entityToBuild,
			...entityToBuild.EntityData,
			EntityAttributes: {
				...entityToBuild.EntityAttributes,
				approvalRequiredToStartExecution : definition?.approvalRequiredToStartExecution ? 'true' : 'false',
				isReRunAllowed: (definition as any)?.isReRunAllowed ? 'true' : 'false',
			},
			Status: status,
			CreatedBy: userName!,
			LastStatusUpdateTime: creationTime,
			CreationTime: creationTime,
			LastStatusUpdatedBy: userName!,
			GroupId: groupId,
		});

		entityValue = {
			...buildEntityValue(
				parentEntity,
				workflowDefinition,
				parentEntity?.EntityData?.[groupIdAttribute] as string || 'SelfServiceWorkflow'
			),
			isWorkflowAutoTriggered: workflowDefinition?.workflowApprovalMetadata?.isWorkflowAutoTriggeredAfterApproval,
		};

		setShowLaunchModel(false);

		//ToDo: Update this to use bulkCreateEntities API when available
		for (const subWorkflowEntity of subWorkflowEntities) {
			const status = isApprovalOnly ? WorkflowStatus.READY : entityStatus;

			const createSubWorkflowEntity = () => onCreateEntityCB({
				path: '/entity',
				method: 'POST',
				body: {
					entity: buildEntityValue(
						{
							EntityName: entityValue.EntityName,
							WorkflowType: subWorkflowEntity.workflowId,
							Region: entityValue.Region,
							EntityType: entityValue.EntityType,
							EntityData: {
								...subWorkflowEntity.entityData,
								startedByParent: entityValue.WorkflowType,
							} as SNOWEntity['EntityData'],
							...subWorkflowEntity.taskMaps,
						} as SNOWEntity,
						subWorkflowEntity.workflowDefinition,
						entityValue.GroupId as string,
						status
					),
					requestId: uuidv4(),
				},
			});

			const response = status === WorkflowStatus.DRAFT
				? await retryOnError(createSubWorkflowEntity, SUB_WORKFLOW_ENTITY_RETRY_DELAYS_MS)
				: await createSubWorkflowEntity();

			if (response?.error) return;
		}

		const successCallback = isApprovalOnly
			? onSuccessSendForApproval
			: onSuccessStartWorkflow;

		await onCreateEntityCB(
			{
				path: '/entity',
				method: 'POST',
				body: {
					entity: entityValue,
					requestId: uuidv4(),
				},
			},
			successCallback
		);
	};

	const onSuccessStartWorkflow =async (entityResponse) => {
		if (hasValidationDefinitions) {
			await onValidationInitiateCB(
				{
					path: '/validation',
					method: 'POST',
					body: {
						entityName: entityResponse?.entityName,
						workflowType,
						requestId: uuidv4(),
					},
					url: SNOW_VALIDATION_SERVICE_ENDPOINT,
				},
				() => onSuccessRedirectionCB(ActionType.VALIDATION)
			);
		} else {
			await onWorkflowStartCB(
				{
					path: `/workflows/${workflowType}/executions`,
					method: 'POST',
					body: {
						entities: [entityResponse?.entityName],
						startedBy: userName,
						requestId: uuidv4(),
						...(watchers.length > 0 && { watchers }),
					},
					url: SNOW_NXT_WORKFLOW_ENDPOINT,
				},
				() => onSuccessRedirectionCB(ActionType.WORKFLOW)
			);
		}
	};

	const onSuccessSendForApproval = async () => {
		const currentConfig = JSON.stringify({ ...entityValue, config:JSON.parse(`"${apiResponse.oldConfig}"`) });
		const newConfig = JSON.stringify({...entityValue, config:JSON.parse(`"${methods.getValues()[IBFR_UPDATE_TASK_ID]?.newConfig}"`) });

		await onSendForApprovalCB(
			{
				path: '/approvals',
				method: 'POST',
				url: SDS_APIGATEWAY_ENDPOINT,
				body: {
					approvalType: ApprovalType.WORKFLOW_EXECUTION,
					approvalConfiguration: {
						currConfig:  workflowName === IBFR_WORKFLOW_NAME ? currentConfig : '{}',
						newConfig: workflowName === IBFR_WORKFLOW_NAME ? newConfig : JSON.stringify(entityValue),
						requestor: entityValue.CreatedBy,
					},
					requestId: uuidv4(),
					approvers: workflowDefinition?.workflowApprovalMetadata?.workflowExecutionApprovers?.map((approverMetadata) => ({
						type: approverMetadata.type,
						alias: approverMetadata.alias,
						level: approverMetadata.level,
					})),
				},
			},
			onSuccessApprovalCB
		);
	};

	const onSuccessApprovalCB = (data) => {
		onSuccessRedirectionCB(ActionType.APPROVAL);

		return data;
	};

	const onSuccessRedirectionCB= (actionType: ActionType = ActionType.WORKFLOW) => {
		const pathName = getWorkflowStartRedirectionLink(
			entityValue.EntityName,
			workflowDefinition?.workflowId || ''
		);
		const entityPageUrl = `${window.location.href}`.replace(
			`${history.location.pathname}`,
			pathName
		);

		setRedirectUrl(`${entityPageUrl}|${actionType}`);
		timeOutRef.current = setTimeout(
			() => history.push(pathName),
			10000
		);
	};

	const injectWatchersIntoSteps = useCallback((steps: WizardProps.Step[]): WizardProps.Step[] =>
		steps.map((step) => {
			const isCommonDataStep = step.title?.toString().startsWith('Workflow Inputs');
			const isReviewStep = step.title?.toString().startsWith('Review');

			if (isCommonDataStep) {
				const watcherFooter = (
					<WatcherInput
						watchers={watchers}
						onAddWatcher={(alias) => setWatchers(prev => [...prev, alias])}
						onRemoveWatcher={(alias) =>
							setWatchers(prev => prev.filter(w => w.toLowerCase() !== alias.toLowerCase()))
						}
						currentUserAlias={userName}
						dataTestId="watchers"
					/>
				);
				return {
					...step,
					content: React.isValidElement(step.content)
						? React.cloneElement(step.content as React.ReactElement<any>, { footer: watcherFooter })
						: (<>{step.content}{watcherFooter}</>),
				};
			}
			if (isReviewStep && watchers.length > 0) {
				return {
					...step,
					content: (
						<SpaceBetween size="l">
							{step.content}
							<Container
								header={<Header>Watchers</Header>}
								data-testid="watchers-review-container"
							>
								{watchers.join(', ')}
							</Container>
						</SpaceBetween>
					),
				};
			}
			return step;
		}), [watchers, userName]);

	return (
		<div>
			<LoadingOverlay hideOverlay={!ifbrLoader} />
			{isAutoPopulating && (
				<LiveRegion>
					<Box
						margin={{ bottom: 'xs', left: 'l' }}
						color="text-body-secondary"
					>
						AutoPopulation in Progress...
					</Box>
					<LoadingBar variant="gen-ai-masked" />
				</LiveRegion>
			)}
			{isEntityDataLoading && entityName && (
				<Alert type="info" statusIconAriaLabel="Info">
					Loading entity data for autopopulation...
				</Alert>
			)}
			{entityDataError && entityName && (
				<Alert type="warning" dismissible statusIconAriaLabel="Warning">
					Failed to load entity data. Starting with empty form. You can fill in the details manually.
				</Alert>
			)}
			<ErrorLoader
				isLoading={isAuthLoading || isWorkflowDataLoading}
				error={authError || workflowDataError}
				isAlert
			/>
			{(validateIFBRInputLoader !== IFBRValidationStatus.NotStarted) &&
				<Alert
					type={ifbrAlert.type as AlertProps.Type}
					header={ifbrAlert.message}
					dismissible={validateIFBRInputLoader !== IFBRValidationStatus.Loading}
					onDismiss={() => {setValidateIFBRInputLoader(IFBRValidationStatus.NotStarted);}}
				/>}
			{autoPopulationError && (<Alert type="error" dismissible onDismiss={() => setAutoPopulationError('')}>
				{autoPopulationError}
			</Alert>)}
			<LoadingOverlay
				hideOverlay={
					!(isEntityCreating || isWorkflowStarting || isValidationInitiating || isSendForApprovalLoading || Boolean(redirectUrl))
				}
			>
				{redirectUrl && (() => {
					const [url, actionType] = redirectUrl.split('|');
					let message = '';
					
					switch (actionType) {
						case ActionType.VALIDATION:
							message = 'Validation initiated successfully';
							break;
						case ActionType.APPROVAL:
							message = 'Approval request sent successfully';
							break;
						case ActionType.WORKFLOW:
						default:
							message = 'Workflow started successfully';
							break;
					}

					return (
						<Alert type="success" statusIconAriaLabel="Success">
							{message} {approvalResponse?.approvalId && (<span> and approvals created successfully. Click <a href={`${AMAZON_APPROVAL_ENDPOINT}/Approval/Details/${approvalResponse?.approvalId}`}>here</a> for the approval page</span>)}. Redirecting to the workflow execution
							page. Please click <a href={url}> here </a> if you are not
							redirected with 10 seconds
						</Alert>
					);
				})()}

			</LoadingOverlay>
			<Flashbar
				items={[...createEntityFlashBarItems, ...workflowStartFlashbarItems, ...validationInitiateFlashbarItems, ...sendForApprovalFlashBarItems]}
			/>
			{!(isAuthLoading || authError) &&
				!(isWorkflowDataLoading || workflowDataError) && (
				<>
					<Modal
						onDismiss={() => setShowCancelModel(false)}
						visible={showCancelModal}
						footer={
							<Box float="right">
								<SpaceBetween direction="horizontal" size="xs">
									<Button
										variant="link"
										onClick={() => setShowCancelModel(false)}
									>
											Cancel
									</Button>
									<Button
										variant="primary"
										onClick={() => (location.href = '../#/')}
									>
											Yes
									</Button>
								</SpaceBetween>
							</Box>
						}
						header="Confirmation"
					>
							You are about to discard your changes. Are you sure?
					</Modal>
					<Modal
						onDismiss={() => setShowLaunchModel(false)}
						visible={showLaunchModal}
						footer={
							<Box float="right">
								<Button
									variant="link"
									onClick={() => setShowLaunchModel(false)}
									disabled={isWorkflowStarting || isValidationInitiating || isSendForApprovalLoading || isEntityCreating}
								>
										Cancel
								</Button>
								<Button
									variant="primary"
									onClick={async () => {
										const outerValues = mainFormMethods.getValues();

										Object.keys(outerValues).forEach(key => {
											methods.setValue(key as any, outerValues[key]);
										});
										await createEntityAndStartWorkflow(methods.getValues() as SNOWEntity);
									}
									}
									disabled={
										isWorkflowDataLoading ||
											isWorkflowStarting ||
											isValidationInitiating ||
											isSendForApprovalLoading ||
											isEntityCreating
									}
								>
									{labels.modalButton}
								</Button>
							</Box>
						}
						header="Confirmation"
					>
							{labels.modalMessage}
					</Modal>
					<div className={isEffectivelyBlocked && !((workflowDefinition as WorkflowDefinition & { allowedDuringPeak?: boolean })?.allowedDuringPeak ?? false) && activeStepIndex === taskDefinitions.length ? 'wizard-execution-blocked' : ''}>
						{customLayout && (
							<Box float="right" margin={{ bottom: 'xs', right: 'l' }}>
								<SpaceBetween direction="horizontal" size="xs">
									<Toggle
										checked={isCustomLayoutViewEnabled}
										onChange={({ detail }) => {
											setIsCustomLayoutViewEnabled(detail.checked);
											setActiveStepIndex(0);
										}}
									>
										{isCustomLayoutViewEnabled ? 'Custom Layout' : 'Default Layout'}
									</Toggle>
									<Popover
										header="Layout Toggle"
										content="Switch between the custom portal layout designed by your workflow admin and the default auto-generated view. This option is temporary while the feature is being tested, and will be removed once users are comfortable with the new layout experience."
										triggerType="custom"
										position="left"
									>
										<Icon name="status-info" variant="link" />
									</Popover>
								</SpaceBetween>
							</Box>
						)}
						<Wizard
							i18nStrings={i18nStrings}
							onNavigate={async ({
								detail: { reason, requestedStepIndex },
							}) => {
								if (
									reason === 'next' ||
											(reason === 'step' &&
												activeStepIndex < requestedStepIndex)
								) {
									setIsLoadingNextStep(true);
									const valid = await methods.trigger();

									if (workflowName === IBFR_WORKFLOW_NAME) {
										const data = {
											...methods.getValues().EntityData,
											EntityName: apiResponse.EntityName,
										};

										methods.setValue('EntityData', data as never);
									}

									if (valid) {
										handleIndexChange(requestedStepIndex);
										setActiveStepIndex(requestedStepIndex);
									}

									setIsLoadingNextStep(false);
								} else {
									setActiveStepIndex(requestedStepIndex);
								}
							}}
							activeStepIndex={activeStepIndex}
							onSubmit={() => {
								const isAllowedDuringPeak = (workflowDefinition as WorkflowDefinition & { allowedDuringPeak?: boolean })?.allowedDuringPeak ?? false;

								if (isEffectivelyBlocked && !isAllowedDuringPeak) return;

								if (workflowName === IBFR_WORKFLOW_NAME) {
									if (ifbrReqeustValid) {
										setShowLaunchModel(true);
									} else {
										setIFBRAlert({
											message: 'Invalid configuration: Cannot start workflow execution',
											type: 'error',
											enabled: true,
											isLoading: false,
										});
									}
								} else {
									setShowLaunchModel(true);
									if (workflowName === IBFR_WORKFLOW_NAME) {
										setIfbrReqeustValid(false);
									}
								}
							}}
							steps={injectWatchersIntoSteps(
								(customLayout && isCustomLayoutViewEnabled)
									? generateStepsFromLayout({
										layout: customLayout,
										taskDefinitions: workflowTreeTasks.allTaskDefinitions,
										subWorkflowGroups: workflowTreeTasks.subWorkflowGroups,
										workflowName,
										workflowIntention: workflowDefinition?.workflowIntention,
										previousValues: baselineValues,
										isIBFR: workflowName === IBFR_WORKFLOW_NAME,
										prefilledValues: apiResponse,
										handleAutoPopulation,
										autopopulatingAttributes: undefined,
									})
									: generateFormSteps(workflowTreeTasks.taskDefinitions, workflowDefinition, activeStepIndex, apiResponse,
										baselineValues, workflowName, handleAutoPopulation, enrichRequirements,
										entityName ? new Set(['EntityName']) : undefined,
										workflowTreeTasks.subWorkflowGroups)
							)}
							isLoadingNextStep={isLoadingNextStep}
							onCancel={() => setShowCancelModel(true)}
						/>
					</div>
				</>
			)}
		</div>
	);
};

const RequirementsPage = () => {
	const { workflowType } = useParams<KeyStringValuePairsObject>();
	const methods = useForm<any>({
		defaultValues: {
			EntityName: '',
			WorkflowType: workflowType,
			Status: 'DRAFT',
			EntityData: {},
			Region: '',
			GroupId: 'SelfServeWorkflows',
			EntityType: '',
		}
	});
	const [taskDefinitions, setTaskDefinitions] = useState<any[]>([]);

	return (
		<FormProvider {...methods}>
			<AutoPopulationProvider
				workflowType={workflowType}
				setValue={methods.setValue}
				getValues={methods.getValues}
				taskDefinitions={taskDefinitions}
			>
				<RequirementsPageContent setTaskDefinitions={setTaskDefinitions} />
			</AutoPopulationProvider>
		</FormProvider>
	);
};

export default RequirementsPage;
